import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatDate, getStatusColor } from '../lib/utils';
import { formatCurrency } from '../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Landmark, Calendar, DollarSign, Percent, Clock, Pencil, Trash2, MoreVertical, TrendingDown, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { CURRENCY_DATA } from '../lib/currency';
import { PermissionBanner } from '../components/PermissionBanner';

const Loans = () => {
  const { currency, canEdit, isViewer } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loans, setLoans] = useState([]);
  const [counterparties, setCounterparties] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [addLenderDialogOpen, setAddLenderDialogOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);

  // Form states
  const [newLoan, setNewLoan] = useState({
    lender_id: '',
    principal_amount: '',
    interest_rate: '',
    currency: 'USD',
    start_date: new Date().toISOString().split('T')[0],
    term_months: '12',
    payment_frequency: 'monthly',
    disbursement_account_id: ''
  });

  const [newPayment, setNewPayment] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    total_amount: '',
    principal_portion: '',
    interest_portion: '',
    payment_account_id: ''
  });

  const [newLender, setNewLender] = useState({
    name: '',
    counterparty_type: 'lender',
    email: '',
    phone: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  // Calculate loan summary stats
  const loanSummary = useMemo(() => {
    const activeLoans = loans.filter(l => l.status === 'active');
    const totalPrincipal = loans.reduce((sum, l) => sum + (l.display_principal || l.principal_amount || 0), 0);
    const totalBalance = loans.reduce((sum, l) => sum + (l.display_balance || l.principal_balance || 0), 0);
    const totalPaid = loans.reduce((sum, l) => sum + (l.total_principal_paid || 0) + (l.total_interest_paid || 0), 0);
    
    // Find upcoming payment
    const today = new Date();
    const upcomingPayments = activeLoans
      .filter(l => l.next_payment_date)
      .map(l => ({
        loan: l,
        date: new Date(l.next_payment_date),
        amount: l.display_monthly_payment || l.monthly_payment || 0
      }))
      .filter(p => p.date >= today)
      .sort((a, b) => a.date - b.date);
    
    const nextPayment = upcomingPayments[0] || null;
    
    // Calculate next 30 days payments - but cap each loan's payment at its outstanding balance
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    const paymentsNext30Days = upcomingPayments
      .filter(p => p.date <= thirtyDaysFromNow)
      .reduce((sum, p) => {
        // Cap payment at the loan's outstanding balance
        const loanBalance = p.loan.display_balance || p.loan.principal_balance || 0;
        const cappedPayment = Math.min(p.amount, loanBalance);
        return sum + cappedPayment;
      }, 0);
    
    // Overall progress
    const overallProgress = totalPrincipal > 0 ? ((totalPrincipal - totalBalance) / totalPrincipal) * 100 : 0;
    
    return {
      totalLoans: loans.length,
      activeLoans: activeLoans.length,
      paidOffLoans: loans.filter(l => l.status === 'paid_off').length,
      totalPrincipal,
      totalBalance,
      totalPaid,
      nextPayment,
      paymentsNext30Days,
      overallProgress
    };
  }, [loans]);

  const loadData = async () => {
    try {
      const [loansRes, cpRes, accRes] = await Promise.all([
        api.get('/loans'),
        api.get('/counterparties?counterparty_type=lender'),
        api.get('/accounts')
      ]);
      setLoans(loansRes.data);
      setCounterparties(cpRes.data);
      setAccounts(accRes.data.filter(a => ['cash', 'bank', 'mobile_money'].includes(a.account_subtype)));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newLoan.lender_id) {
      toast.error('Please select a lender');
      return;
    }
    if (!newLoan.disbursement_account_id && !editingLoan) {
      toast.error('Please select a disbursement account');
      return;
    }
    if (!newLoan.principal_amount || parseFloat(newLoan.principal_amount) <= 0) {
      toast.error('Please enter a valid principal amount');
      return;
    }
    
    try {
      if (editingLoan) {
        // Update existing loan
        await api.put(`/loans/${editingLoan.id}`, {
          lender_id: newLoan.lender_id,
          principal_amount: parseFloat(newLoan.principal_amount),
          interest_rate: parseFloat(newLoan.interest_rate) || 0,
          currency: newLoan.currency,
          term_months: parseInt(newLoan.term_months)
        });
        toast.success('Loan updated successfully!');
      } else {
        // Create new loan
        await api.post('/loans', {
          ...newLoan,
          principal_amount: parseFloat(newLoan.principal_amount),
          interest_rate: parseFloat(newLoan.interest_rate) || 0,
          term_months: parseInt(newLoan.term_months)
        });
        toast.success('Loan created successfully!');
      }
      setAddDialogOpen(false);
      resetLoanForm();
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join(', '));
      } else {
        toast.error(detail || 'Failed to create loan');
      }
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    
    try {
      await api.post(`/loans/${selectedLoan.id}/payments`, {
        ...newPayment,
        total_amount: parseFloat(newPayment.total_amount),
        principal_portion: parseFloat(newPayment.principal_portion),
        interest_portion: parseFloat(newPayment.interest_portion)
      });
      toast.success('Payment recorded!');
      setPaymentDialogOpen(false);
      // Refresh loan details
      const res = await api.get(`/loans/${selectedLoan.id}`);
      setSelectedLoan(res.data);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleAddLender = async () => {
    if (!newLender.name) {
      toast.error('Please enter a lender name');
      return;
    }
    try {
      const res = await api.post('/counterparties', newLender);
      toast.success('Lender added successfully');
      setAddLenderDialogOpen(false);
      setNewLoan({ ...newLoan, lender_id: res.data.id });
      setNewLender({ name: '', counterparty_type: 'lender', email: '', phone: '' });
      // Refresh counterparties
      const cpRes = await api.get('/counterparties');
      setCounterparties(cpRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add lender');
    }
  };

  const selectLoan = async (loan) => {
    try {
      const res = await api.get(`/loans/${loan.id}`);
      setSelectedLoan(res.data);
    } catch (error) {
      toast.error('Failed to load loan details');
    }
  };

  const handleDeleteLoan = async (loanId) => {
    if (!confirm('Are you sure you want to delete this loan? This cannot be undone.')) return;
    try {
      await api.delete(`/loans/${loanId}`);
      toast.success('Loan deleted successfully');
      setSelectedLoan(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete loan');
    }
  };

  const openEditLoan = (loan) => {
    setEditingLoan(loan);
    setNewLoan({
      lender_id: loan.lender_id || '',
      principal_amount: loan.principal_amount?.toString() || '',
      interest_rate: loan.interest_rate?.toString() || '',
      currency: loan.currency || 'USD',
      start_date: loan.start_date || new Date().toISOString().split('T')[0],
      term_months: loan.term_months?.toString() || '12',
      payment_frequency: loan.payment_frequency || 'monthly',
      disbursement_account_id: loan.disbursement_account_id || ''
    });
    setAddDialogOpen(true);
  };

  const resetLoanForm = () => {
    setNewLoan({
      lender_id: '',
      principal_amount: '',
      interest_rate: '',
      currency: currency || 'USD',
      start_date: new Date().toISOString().split('T')[0],
      term_months: '12',
      payment_frequency: 'monthly',
      disbursement_account_id: ''
    });
    setEditingLoan(null);
  };

  // Auto-calculate split when total changes
  const handleTotalAmountChange = (value) => {
    const total = parseFloat(value) || 0;
    // Use schedule suggestion if available
    const nextScheduleItem = selectedLoan?.schedule?.find(s => s.status === 'pending');
    if (nextScheduleItem && total === nextScheduleItem.total_amount) {
      setNewPayment({
        ...newPayment,
        total_amount: value,
        principal_portion: nextScheduleItem.principal_amount.toString(),
        interest_portion: nextScheduleItem.interest_amount.toString()
      });
    } else {
      // Simple split based on loan terms
      const rate = selectedLoan?.interest_rate || 0;
      const monthlyRate = rate / 100 / 12;
      const balance = selectedLoan?.principal_balance || 0;
      const interest = balance * monthlyRate;
      const principal = Math.max(0, total - interest);
      
      setNewPayment({
        ...newPayment,
        total_amount: value,
        principal_portion: principal.toFixed(2),
        interest_portion: interest.toFixed(2)
      });
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            Loans
          </h1>
          <p className="text-[hsl(var(--muted-ink))] mt-1">
            Track and manage your loan obligations
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
            data-testid="add-loan-button"
          >
            <Plus size={18} className="mr-2" />
            Add Loan
          </Button>
        )}
      </div>

      {/* Permission Banner for Viewers */}
      <PermissionBanner 
        show={isViewer} 
        message="You have read-only access to loans. Contact an admin to request edit permissions."
      />

      {/* Loan Summary Dashboard */}
      {loans.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="loan-summary-dashboard">
          {/* Total Outstanding */}
          <Card className="bg-gradient-to-br from-red-50 to-red-100/50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-lg bg-red-100 text-red-600">
                  <TrendingDown size={18} />
                </div>
                <span className="text-sm text-red-700 font-medium">Total Outstanding</span>
              </div>
              <p className="text-2xl font-bold text-red-700 numbered" data-testid="loan-total-outstanding">
                {formatCurrency(loanSummary.totalBalance, currency)}
              </p>
              <p className="text-xs text-red-600 mt-1">
                from {formatCurrency(loanSummary.totalPrincipal, currency)} borrowed
              </p>
            </CardContent>
          </Card>

          {/* Overall Progress */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                  <CheckCircle2 size={18} />
                </div>
                <span className="text-sm text-blue-700 font-medium">Repayment Progress</span>
              </div>
              <p className="text-2xl font-bold text-blue-700" data-testid="loan-progress">
                {loanSummary.overallProgress.toFixed(1)}%
              </p>
              <Progress value={loanSummary.overallProgress} className="h-2 mt-2" />
            </CardContent>
          </Card>

          {/* Next Payment */}
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-lg bg-amber-100 text-amber-600">
                  <Calendar size={18} />
                </div>
                <span className="text-sm text-amber-700 font-medium">Next Payment</span>
              </div>
              {loanSummary.nextPayment ? (
                <>
                  <p className="text-2xl font-bold text-amber-700 numbered" data-testid="loan-next-payment">
                    {formatCurrency(loanSummary.nextPayment.amount, currency)}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Due {formatDate(loanSummary.nextPayment.date)}
                  </p>
                </>
              ) : (
                <p className="text-lg text-amber-600">No upcoming payments</p>
              )}
            </CardContent>
          </Card>

          {/* Loan Summary */}
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                  <Landmark size={18} />
                </div>
                <span className="text-sm text-emerald-700 font-medium">Loan Overview</span>
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold text-emerald-700" data-testid="loan-active-count">
                  {loanSummary.activeLoans}
                </p>
                <span className="text-sm text-emerald-600">active</span>
              </div>
              <p className="text-xs text-emerald-600 mt-1">
                {loanSummary.paidOffLoans} paid off • {loanSummary.totalLoans} total
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Due Soon Alert */}
      {loanSummary.paymentsNext30Days > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-full bg-amber-100">
              <AlertCircle size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-800">Upcoming Payments</p>
              <p className="text-sm text-amber-700">
                You have <span className="font-semibold">{formatCurrency(loanSummary.paymentsNext30Days, currency)}</span> in loan payments due within the next 30 days
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Loan List */}
        <div className="lg:col-span-1 space-y-4">
          {loading ? (
            [1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-5 bg-gray-100 rounded w-1/2 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-3/4" />
                </CardContent>
              </Card>
            ))
          ) : loans.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Landmark size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="font-medium mb-1">No loans yet</h3>
                <p className="text-sm text-[hsl(var(--muted-ink))]">Add your first loan to get started</p>
              </CardContent>
            </Card>
          ) : (
            loans.map((loan) => (
              <Card
                key={loan.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedLoan?.id === loan.id ? 'ring-2 ring-[hsl(var(--brand-500))]' : ''
                }`}
                onClick={() => selectLoan(loan)}
                data-testid="loan-card"
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div onClick={() => selectLoan(loan)}>
                      <h3 className="font-medium">{loan.lender?.name || 'Unknown Lender'}</h3>
                      <p className="text-sm text-[hsl(var(--muted-ink))]">{loan.loan_number}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(loan.status)}>
                        {loan.status.replace('_', ' ')}
                      </Badge>
                      {canEdit && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" data-testid={`loan-menu-${loan.id}`}>
                              <MoreVertical size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); openEditLoan(loan); }} 
                              data-testid={`edit-loan-${loan.id}`}
                            >
                              <Pencil size={14} className="mr-2" />
                              Edit Loan
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); handleDeleteLoan(loan.id); }} 
                              className="text-red-600"
                              data-testid={`delete-loan-${loan.id}`}
                            >
                              <Trash2 size={14} className="mr-2" />
                              Delete Loan
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                    <div>
                      <p className="text-[hsl(var(--muted-ink))]">Principal</p>
                      <p className="font-semibold numbered">{formatCurrency(loan.display_principal || loan.principal_amount, loan.display_currency || currency)}</p>
                    </div>
                    <div>
                      <p className="text-[hsl(var(--muted-ink))]">Balance</p>
                      <p className="font-semibold numbered text-red-600">{formatCurrency(loan.display_balance || loan.principal_balance, loan.display_currency || currency)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Loan Details */}
        <div className="lg:col-span-2">
          {selectedLoan ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{selectedLoan.lender?.name}</CardTitle>
                  <p className="text-sm text-[hsl(var(--muted-ink))]">{selectedLoan.loan_number}</p>
                </div>
                {selectedLoan.status === 'active' && canEdit && (
                  <Button
                    onClick={() => {
                      const next = selectedLoan.schedule?.find(s => s.status === 'pending');
                      setNewPayment({
                        payment_date: new Date().toISOString().split('T')[0],
                        total_amount: next?.total_amount?.toString() || '',
                        principal_portion: next?.principal_amount?.toString() || '',
                        interest_portion: next?.interest_amount?.toString() || '',
                        payment_account_id: accounts[0]?.id || ''
                      });
                      setPaymentDialogOpen(true);
                    }}
                    className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
                    data-testid="make-payment-button"
                  >
                    <DollarSign size={18} className="mr-2" />
                    Make Payment
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="overview">
                  <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="schedule">Schedule</TabsTrigger>
                    <TabsTrigger value="payments">Payments</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-6 mt-4">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-[hsl(var(--muted-ink))] text-sm mb-1">
                          <DollarSign size={14} />
                          Principal
                        </div>
                        <p className="text-xl font-semibold numbered">{formatCurrency(selectedLoan.display_principal || selectedLoan.principal_amount, selectedLoan.display_currency || currency)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-[hsl(var(--muted-ink))] text-sm mb-1">
                          <Percent size={14} />
                          Interest Rate
                        </div>
                        <p className="text-xl font-semibold">{selectedLoan.interest_rate}%</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-[hsl(var(--muted-ink))] text-sm mb-1">
                          <Clock size={14} />
                          Term
                        </div>
                        <p className="text-xl font-semibold">{selectedLoan.term_months} months</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-red-600 text-sm mb-1">
                          <DollarSign size={14} />
                          Balance
                        </div>
                        <p className="text-xl font-semibold numbered text-red-600">{formatCurrency(selectedLoan.display_balance || selectedLoan.principal_balance, selectedLoan.display_currency || currency)}</p>
                      </div>
                    </div>

                    {/* Progress */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[hsl(var(--muted-ink))]">Repayment Progress</span>
                        <span className="font-medium">
                          {((1 - selectedLoan.principal_balance / selectedLoan.principal_amount) * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[hsl(var(--brand-500))] transition-all"
                          style={{ width: `${(1 - selectedLoan.principal_balance / selectedLoan.principal_amount) * 100}%` }}
                        />
                      </div>
                    </div>

                    {/* Totals */}
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                      <div>
                        <p className="text-sm text-[hsl(var(--muted-ink))]">Total Principal Paid</p>
                        <p className="text-lg font-semibold numbered text-green-600">{formatCurrency(selectedLoan.total_principal_paid, selectedLoan.display_currency || currency)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[hsl(var(--muted-ink))]">Total Interest Paid</p>
                        <p className="text-lg font-semibold numbered text-amber-600">{formatCurrency(selectedLoan.total_interest_paid, selectedLoan.display_currency || currency)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[hsl(var(--muted-ink))]">Next Payment</p>
                        <p className="text-lg font-semibold">
                          {selectedLoan.next_payment_date ? formatDate(selectedLoan.next_payment_date) : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="schedule" className="mt-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 font-medium">#</th>
                            <th className="text-left py-3 font-medium">Due Date</th>
                            <th className="text-right py-3 font-medium">Principal</th>
                            <th className="text-right py-3 font-medium">Interest</th>
                            <th className="text-right py-3 font-medium">Total</th>
                            <th className="text-right py-3 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedLoan.schedule?.map((item) => (
                            <tr key={item.id} className="border-b last:border-0">
                              <td className="py-3">{item.payment_number}</td>
                              <td className="py-3">{formatDate(item.due_date)}</td>
                              <td className="py-3 text-right numbered">{formatCurrency(item.display_principal || item.principal_amount, selectedLoan.display_currency || currency)}</td>
                              <td className="py-3 text-right numbered">{formatCurrency(item.display_interest || item.interest_amount, selectedLoan.display_currency || currency)}</td>
                              <td className="py-3 text-right numbered font-medium">{formatCurrency(item.display_total || item.total_amount, selectedLoan.display_currency || currency)}</td>
                              <td className="py-3 text-right">
                                <Badge className={getStatusColor(item.status)}>
                                  {item.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>

                  <TabsContent value="payments" className="mt-4">
                    {selectedLoan.payments?.length > 0 ? (
                      <div className="space-y-3">
                        {selectedLoan.payments.map((payment) => (
                          <div key={payment.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium">{formatDate(payment.payment_date)}</p>
                              <p className="text-sm text-[hsl(var(--muted-ink))]">
                                Principal: {formatCurrency(payment.display_principal || payment.principal_portion, selectedLoan.display_currency || currency)} | Interest: {formatCurrency(payment.display_interest || payment.interest_portion, selectedLoan.display_currency || currency)}
                              </p>
                            </div>
                            <p className="text-lg font-semibold numbered text-green-600">
                              {formatCurrency(payment.display_total || payment.total_amount, selectedLoan.display_currency || currency)}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-[hsl(var(--muted-ink))]">
                        No payments recorded yet
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Landmark size={64} className="mx-auto text-gray-200 mb-4" />
                <h3 className="font-medium text-lg mb-1">Select a loan</h3>
                <p className="text-[hsl(var(--muted-ink))]">Click on a loan from the list to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add Loan Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => { setAddDialogOpen(open); if (!open) resetLoanForm(); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingLoan ? 'Edit Loan' : 'Add New Loan'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLoan} className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <Label>Lender</Label>
                <Button 
                  type="button" 
                  variant="link" 
                  size="sm" 
                  className="h-auto p-0 text-[hsl(var(--brand-600))]"
                  onClick={() => setAddLenderDialogOpen(true)}
                  data-testid="quick-add-lender-btn"
                >
                  <Plus size={14} className="mr-1" />
                  Add New Lender
                </Button>
              </div>
              <Select
                value={newLoan.lender_id}
                onValueChange={(value) => setNewLoan({ ...newLoan, lender_id: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select lender" />
                </SelectTrigger>
                <SelectContent>
                  {counterparties.filter(cp => cp.counterparty_type === 'lender').length === 0 ? (
                    <SelectItem value="none" disabled>No lenders - click Add New above</SelectItem>
                  ) : (
                    counterparties.filter(cp => cp.counterparty_type === 'lender').map((cp) => (
                      <SelectItem key={cp.id} value={cp.id}>{cp.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Principal Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="10,000.00"
                  value={newLoan.principal_amount}
                  onChange={(e) => setNewLoan({ ...newLoan, principal_amount: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select
                  value={newLoan.currency}
                  onValueChange={(value) => setNewLoan({ ...newLoan, currency: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="loan-currency-select">
                    <SelectValue placeholder="Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CURRENCY_DATA).map(([code, data]) => (
                      <SelectItem key={code} value={code}>
                        {data.symbol} {code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Interest Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="12.00"
                  value={newLoan.interest_rate}
                  onChange={(e) => setNewLoan({ ...newLoan, interest_rate: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newLoan.start_date}
                  onChange={(e) => setNewLoan({ ...newLoan, start_date: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Term (Months)</Label>
                <Input
                  type="number"
                  placeholder="12"
                  value={newLoan.term_months}
                  onChange={(e) => setNewLoan({ ...newLoan, term_months: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Disbursement Account *</Label>
              <Select
                value={newLoan.disbursement_account_id}
                onValueChange={(value) => setNewLoan({ ...newLoan, disbursement_account_id: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Where did you receive the money?" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setAddDialogOpen(false); resetLoanForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white">
                {editingLoan ? 'Update Loan' : 'Create Loan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={newPayment.payment_date}
                onChange={(e) => setNewPayment({ ...newPayment, payment_date: e.target.value })}
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label>Total Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={newPayment.total_amount}
                onChange={(e) => handleTotalAmountChange(e.target.value)}
                required
                className="mt-1 text-lg"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Principal Portion</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPayment.principal_portion}
                  onChange={(e) => setNewPayment({ ...newPayment, principal_portion: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Interest Portion</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPayment.interest_portion}
                  onChange={(e) => setNewPayment({ ...newPayment, interest_portion: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Payment Account</Label>
              <Select
                value={newPayment.payment_account_id}
                onValueChange={(value) => setNewPayment({ ...newPayment, payment_account_id: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white" data-testid="loan-payment-submit-button">
                Record Payment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Add Lender Dialog */}
      <Dialog open={addLenderDialogOpen} onOpenChange={setAddLenderDialogOpen}>
        <DialogContent data-testid="quick-add-lender-dialog">
          <DialogHeader>
            <DialogTitle>Add New Lender</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Lender Name *</Label>
              <Input
                value={newLender.name}
                onChange={(e) => setNewLender({...newLender, name: e.target.value})}
                placeholder="Bank name or lender name"
                className="mt-1"
                data-testid="lender-name-input"
              />
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input
                type="email"
                value={newLender.email}
                onChange={(e) => setNewLender({...newLender, email: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input
                value={newLender.phone}
                onChange={(e) => setNewLender({...newLender, phone: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setAddLenderDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddLender}
              className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
              data-testid="save-lender-btn"
            >
              Add Lender
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Loans;
